import React, { useEffect, useState, useCallback } from "react";
import { ToastContainer } from "react-toastify";
import {
  Box,
  Typography,
  useTheme,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Container,
  Avatar,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
} from "@mui/material";
import {
  Search,
  Mail,
  MailOpen,
  MessageSquare,
  CheckCircle2,
  Clock,
  User,
  Calendar,
  RefreshCw,
  Eye,
  Send,
  Reply,
} from "lucide-react";
import { tokens } from "../../theme";
import { useLoadingSpinner } from "../../utils/components/LoadingSpinnerProvider";
import { getAllInquiries, updateInquiryStatus, replyToInquiry } from "./ContactInquiriesService";
import KeycloakService from "../config/KeycloakService";

// ─── Status config ──────────────────────────────────────────
const STATUS_CONFIG = {
  NEW: { label: "New", color: "#ef4444", icon: <Mail size={14} />, bgAlpha: 0.12 },
  READ: { label: "Read", color: "#f59e0b", icon: <MailOpen size={14} />, bgAlpha: 0.12 },
  RESPONDED: { label: "Responded", color: "#10b981", icon: <MessageSquare size={14} />, bgAlpha: 0.12 },
  CLOSED: { label: "Closed", color: "#6b7280", icon: <CheckCircle2 size={14} />, bgAlpha: 0.12 },
};

const STATUS_ORDER = ["NEW", "READ", "RESPONDED", "CLOSED"];

const fmtDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

// ─── Status Chip ────────────────────────────────────────────
const StatusChip = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.NEW;
  return (
    <Chip
      icon={cfg.icon}
      label={cfg.label}
      size="small"
      sx={{
        bgcolor: alpha(cfg.color, cfg.bgAlpha),
        color: cfg.color,
        fontWeight: 700,
        fontSize: "12px",
        "& .MuiChip-icon": { color: cfg.color },
      }}
    />
  );
};

// ─── Detail Dialog ──────────────────────────────────────────
const InquiryDetailDialog = ({ open, inquiry, onClose, onStatusChange, onReply, colors, isDark }) => {
  const [replyText, setReplyText] = useState("");
  const [showReplyForm, setShowReplyForm] = useState(false);

  // Reset form when dialog opens with a new inquiry
  React.useEffect(() => {
    if (open) {
      setReplyText("");
      setShowReplyForm(false);
    }
  }, [open, inquiry?.id]);

  if (!inquiry) return null;

  const nextStatuses = STATUS_ORDER.filter((s) => s !== inquiry.status);

  const handleSendReply = () => {
    if (replyText.trim()) {
      onReply(inquiry.id, replyText.trim());
    }
  };

  const inputBg = isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)";
  const inputBorder = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  const sectionBox = {
    p: "14px 18px",
    borderRadius: "12px",
    bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
    mb: 2,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          bgcolor: isDark ? "#1f2937" : "#fff",
          backgroundImage: "none",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight={700} sx={{ color: colors.grey[100], fontFamily: "Josefin Sans" }}>
            Inquiry Details
          </Typography>
          <StatusChip status={inquiry.status} />
        </Box>
      </DialogTitle>
      <Divider sx={{ borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)" }} />
      <DialogContent sx={{ pt: 3 }}>
        {/* Sender info */}
        <Box display="flex" alignItems="center" gap="14px" mb={3}>
          <Avatar sx={{ bgcolor: alpha("#3b82f6", 0.12), color: "#3b82f6", width: 48, height: 48 }}>
            <User size={24} />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: colors.grey[100] }}>
              {inquiry.name}
            </Typography>
            <Typography variant="body2" sx={{ color: colors.grey[300] }}>
              {inquiry.email}
            </Typography>
          </Box>
        </Box>

        {/* Subject */}
        <Box sx={sectionBox}>
          <Typography variant="caption" sx={{ color: colors.grey[500], fontWeight: 600, display: "block", mb: "4px" }}>
            SUBJECT
          </Typography>
          <Typography variant="body1" fontWeight={600} sx={{ color: colors.grey[100] }}>
            {inquiry.subject}
          </Typography>
        </Box>

        {/* Message */}
        <Box sx={{ ...sectionBox, maxHeight: 200, overflow: "auto" }}>
          <Typography variant="caption" sx={{ color: colors.grey[500], fontWeight: 600, display: "block", mb: "4px" }}>
            MESSAGE
          </Typography>
          <Typography variant="body2" sx={{ color: colors.grey[100], whiteSpace: "pre-wrap", lineHeight: 1.7 }}>
            {inquiry.message}
          </Typography>
        </Box>

        {/* Dates */}
        <Box display="flex" gap="16px" flexWrap="wrap" mb={2}>
          <Box display="flex" alignItems="center" gap="6px">
            <Calendar size={14} color={colors.grey[500]} />
            <Typography variant="caption" sx={{ color: colors.grey[500] }}>
              Received: {fmtDate(inquiry.createdAt)}
            </Typography>
          </Box>
          {inquiry.respondedAt && (
            <Box display="flex" alignItems="center" gap="6px">
              <MessageSquare size={14} color="#10b981" />
              <Typography variant="caption" sx={{ color: "#10b981" }}>
                Responded: {fmtDate(inquiry.respondedAt)}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Reply compose form */}
        {showReplyForm ? (
          <Box
            sx={{
              p: "16px 18px",
              borderRadius: "12px",
              bgcolor: isDark ? alpha("#3b82f6", 0.06) : alpha("#3b82f6", 0.03),
              border: `1px solid ${alpha("#3b82f6", 0.2)}`,
            }}
          >
            <Box display="flex" alignItems="center" gap="6px" mb="10px">
              <Send size={14} color="#3b82f6" />
              <Typography variant="caption" sx={{ color: "#3b82f6", fontWeight: 700 }}>
                COMPOSE REPLY
              </Typography>
              <Typography variant="caption" sx={{ color: colors.grey[500], ml: "auto" }}>
                This will be emailed to {inquiry.email}
              </Typography>
            </Box>
            <TextField
              multiline
              minRows={4}
              maxRows={10}
              fullWidth
              placeholder={`Hi ${inquiry.name},\n\nThank you for reaching out to Le Prestige Hall...\n\n`}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              sx={{
                mb: "12px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  bgcolor: inputBg,
                  "& fieldset": { borderColor: inputBorder },
                  "&:hover fieldset": { borderColor: alpha("#3b82f6", 0.4) },
                  "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
                },
                "& .MuiInputBase-input": { color: colors.grey[100], fontSize: "14px", lineHeight: 1.7 },
              }}
            />
            <Box display="flex" gap="8px" justifyContent="flex-end">
              <Button
                size="small"
                onClick={() => { setShowReplyForm(false); setReplyText(""); }}
                sx={{ textTransform: "none", color: colors.grey[300], fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<Send size={16} />}
                disabled={!replyText.trim()}
                onClick={handleSendReply}
                sx={{
                  bgcolor: "#3b82f6",
                  color: "#fff",
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: "10px",
                  px: "20px",
                  "&:hover": { bgcolor: "#2563eb" },
                  "&.Mui-disabled": { bgcolor: alpha("#3b82f6", 0.3), color: alpha("#fff", 0.5) },
                }}
              >
                Send Reply
              </Button>
            </Box>
          </Box>
        ) : (
          <Button
            variant="outlined"
            startIcon={<Reply size={16} />}
            onClick={() => setShowReplyForm(true)}
            sx={{
              borderColor: alpha("#3b82f6", 0.4),
              color: "#3b82f6",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "10px",
              "&:hover": { bgcolor: alpha("#3b82f6", 0.08), borderColor: "#3b82f6" },
            }}
          >
            {inquiry.status === "RESPONDED" ? "Send Follow-up" : "Reply to Inquiry"}
          </Button>
        )}
      </DialogContent>
      <Divider sx={{ borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)" }} />
      <DialogActions sx={{ p: "16px 24px", gap: "8px" }}>
        {nextStatuses.map((st) => {
          const cfg = STATUS_CONFIG[st];
          return (
            <Button
              key={st}
              variant="outlined"
              size="small"
              startIcon={cfg.icon}
              onClick={() => onStatusChange(inquiry.id, st)}
              sx={{
                borderColor: alpha(cfg.color, 0.4),
                color: cfg.color,
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "10px",
                "&:hover": { bgcolor: alpha(cfg.color, 0.08), borderColor: cfg.color },
              }}
            >
              Mark {cfg.label}
            </Button>
          );
        })}
        <Box flex={1} />
        <Button
          onClick={onClose}
          sx={{ textTransform: "none", color: colors.grey[300], fontWeight: 600 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main Component ─────────────────────────────────────────
const ContactInquiries = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const setIsLoading = useLoadingSpinner();

  const [data, setData] = useState({ content: [], page: { totalElements: 0 } });
  const [params, setParams] = useState({ page: 0, size: 20 });
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const totalElements = data?.page?.totalElements ?? data?.totalElements ?? 0;

  const fetchData = useCallback(() => {
    setIsLoading(true);
    const apiParams = { ...params };
    if (statusFilter !== "ALL") apiParams.status = statusFilter;
    getAllInquiries(setData, apiParams, setIsLoading);
  }, [params, statusFilter, setIsLoading, refreshTick]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStatusChange = (id, newStatus) => {
    updateInquiryStatus(id, newStatus, () => {
      setRefreshTick((t) => t + 1);
      setDetailOpen(false);
    }, setIsLoading);
  };

  const handleReply = (id, message) => {
    const adminName = `${KeycloakService.getUserFirstName()} ${KeycloakService.getUserLastName()}`;
    replyToInquiry(id, message, adminName, () => {
      setRefreshTick((t) => t + 1);
      setDetailOpen(false);
    }, setIsLoading);
  };

  const openDetail = (inquiry) => {
    setSelectedInquiry(inquiry);
    setDetailOpen(true);
    // Auto-mark as READ if NEW
    if (inquiry.status === "NEW") {
      updateInquiryStatus(inquiry.id, "READ", () => {
        setRefreshTick((t) => t + 1);
      }, () => {});
    }
  };

  const inquiries = data?.content ?? [];

  // Client-side search filter on top of server-side status filter
  const filtered = inquiries.filter((inq) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      inq.name?.toLowerCase().includes(q) ||
      inq.email?.toLowerCase().includes(q) ||
      inq.subject?.toLowerCase().includes(q) ||
      inq.message?.toLowerCase().includes(q)
    );
  });

  // Status counts from current data
  const counts = { ALL: inquiries.length, NEW: 0, READ: 0, RESPONDED: 0, CLOSED: 0 };
  inquiries.forEach((inq) => {
    counts[inq.status] = (counts[inq.status] || 0) + 1;
  });

  const cardSx = {
    bgcolor: isDark ? colors.primary[400] : "#fff",
    borderRadius: "16px",
    border: `1px solid ${isDark ? colors.primary[300] : colors.grey[200]}`,
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    overflow: "hidden",
  };

  return (
    <Container maxWidth="xl" sx={{ mt: "40px", pb: "80px" }}>
      <ToastContainer />

      {/* ─── Header ─────────────────────────────────────── */}
      <Box
        mb="32px"
        sx={{
          background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
          padding: "32px",
          borderRadius: "24px",
          border: `1px solid ${isDark ? colors.primary[400] : colors.grey[200]}`,
          boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb="20px">
          <Box>
            <Typography variant="h2" fontWeight="bold" sx={{ color: colors.grey[100], mb: "4px", fontFamily: "Josefin Sans" }}>
              Contact Inquiries
            </Typography>
            <Typography variant="h5" color={isDark ? colors.greenAccent[500] : colors.greenAccent[300]} fontWeight="500">
              {totalElements} total inquiries · {counts.NEW} unread
            </Typography>
          </Box>
          <Tooltip title="Refresh">
            <IconButton
              onClick={() => setRefreshTick((t) => t + 1)}
              sx={{
                bgcolor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                borderRadius: "12px",
                color: colors.greenAccent[500],
                "&:hover": { bgcolor: alpha(colors.greenAccent[500], 0.15) },
              }}
            >
              <RefreshCw size={20} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Filters row */}
        <Box display="flex" gap="12px" alignItems="center" flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Search by name, email, subject…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} color={colors.grey[500]} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 320,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                bgcolor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
                "& fieldset": { borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)" },
              },
              "& .MuiInputBase-input": { color: colors.grey[100], fontWeight: 500 },
            }}
          />

          <ToggleButtonGroup
            value={statusFilter}
            exclusive
            onChange={(e, val) => {
              if (val !== null) {
                setStatusFilter(val);
                setParams((p) => ({ ...p, page: 0 }));
              }
            }}
            size="small"
            sx={{
              bgcolor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
              borderRadius: "12px",
              p: "3px",
              "& .MuiToggleButton-root": {
                border: "none",
                borderRadius: "9px !important",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                px: "14px",
                color: colors.grey[300],
                "&.Mui-selected": {
                  bgcolor: colors.greenAccent[500],
                  color: isDark ? colors.primary[500] : "#fff",
                  "&:hover": { bgcolor: colors.greenAccent[400] },
                },
              },
            }}
          >
            <ToggleButton value="ALL">All ({counts.ALL})</ToggleButton>
            {STATUS_ORDER.map((st) => (
              <ToggleButton key={st} value={st}>
                {STATUS_CONFIG[st].label} ({counts[st]})
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* ─── Inquiry Cards ──────────────────────────────── */}
      {filtered.length === 0 ? (
        <Box sx={{ ...cardSx, p: "60px", textAlign: "center" }}>
          <Mail size={48} color={colors.grey[500]} style={{ marginBottom: 16, opacity: 0.5 }} />
          <Typography variant="h5" fontWeight={600} sx={{ color: colors.grey[300], mb: "8px" }}>
            {searchQuery ? "No matching inquiries" : "No inquiries found"}
          </Typography>
          <Typography variant="body2" sx={{ color: colors.grey[500] }}>
            {searchQuery ? "Try a different search term" : statusFilter !== "ALL" ? "No inquiries with this status" : "Contact inquiries from the website will appear here"}
          </Typography>
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gap="12px">
          {filtered.map((inq) => (
            <Box
              key={inq.id}
              sx={{
                ...cardSx,
                p: "20px 24px",
                display: "flex",
                alignItems: "center",
                gap: "16px",
                cursor: "pointer",
                transition: "all 0.2s",
                borderLeft: `4px solid ${STATUS_CONFIG[inq.status]?.color || "#6b7280"}`,
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                },
              }}
              onClick={() => openDetail(inq)}
            >
              {/* Icon */}
              <Avatar
                sx={{
                  bgcolor: alpha(STATUS_CONFIG[inq.status]?.color || "#6b7280", 0.12),
                  color: STATUS_CONFIG[inq.status]?.color || "#6b7280",
                  width: 44,
                  height: 44,
                }}
              >
                {inq.status === "NEW" ? <Mail size={20} /> : inq.status === "READ" ? <MailOpen size={20} /> : inq.status === "RESPONDED" ? <MessageSquare size={20} /> : <CheckCircle2 size={20} />}
              </Avatar>

              {/* Content */}
              <Box flex={1} minWidth={0}>
                <Box display="flex" alignItems="center" gap="8px" mb="4px">
                  <Typography variant="body1" fontWeight={inq.status === "NEW" ? 800 : 600} sx={{ color: colors.grey[100] }} noWrap>
                    {inq.subject}
                  </Typography>
                  <StatusChip status={inq.status} />
                  {inq.respondedAt && (
                    <Tooltip title="Replied">
                      <Reply size={14} color="#10b981" />
                    </Tooltip>
                  )}
                </Box>
                <Box display="flex" alignItems="center" gap="16px">
                  <Box display="flex" alignItems="center" gap="4px">
                    <User size={13} color={colors.grey[500]} />
                    <Typography variant="caption" sx={{ color: colors.grey[300], fontWeight: 600 }}>
                      {inq.name}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: colors.grey[500] }}>
                    {inq.email}
                  </Typography>
                </Box>
              </Box>

              {/* Date + actions */}
              <Box textAlign="right" flexShrink={0}>
                <Box display="flex" alignItems="center" gap="4px" justifyContent="flex-end" mb="4px">
                  <Clock size={13} color={colors.grey[500]} />
                  <Typography variant="caption" sx={{ color: colors.grey[500], fontWeight: 500 }}>
                    {fmtDate(inq.createdAt)}
                  </Typography>
                </Box>
                <Tooltip title="View details">
                  <IconButton
                    size="small"
                    sx={{ color: colors.grey[300] }}
                    onClick={(e) => { e.stopPropagation(); openDetail(inq); }}
                  >
                    <Eye size={16} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* ─── Pagination ─────────────────────────────────── */}
      {totalElements > params.size && (
        <Box display="flex" justifyContent="center" mt="24px" gap="8px">
          <Button
            variant="outlined"
            size="small"
            disabled={params.page === 0}
            onClick={() => setParams((p) => ({ ...p, page: p.page - 1 }))}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, color: colors.grey[100], borderColor: isDark ? colors.primary[300] : colors.grey[200] }}
          >
            Previous
          </Button>
          <Chip
            label={`Page ${params.page + 1} of ${Math.ceil(totalElements / params.size)}`}
            sx={{ bgcolor: isDark ? colors.primary[300] : colors.grey[200], color: colors.grey[100], fontWeight: 600 }}
          />
          <Button
            variant="outlined"
            size="small"
            disabled={(params.page + 1) * params.size >= totalElements}
            onClick={() => setParams((p) => ({ ...p, page: p.page + 1 }))}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, color: colors.grey[100], borderColor: isDark ? colors.primary[300] : colors.grey[200] }}
          >
            Next
          </Button>
        </Box>
      )}

      {/* ─── Detail Dialog ──────────────────────────────── */}
      <InquiryDetailDialog
        open={detailOpen}
        inquiry={selectedInquiry}
        onClose={() => setDetailOpen(false)}
        onStatusChange={handleStatusChange}
        onReply={handleReply}
        colors={colors}
        isDark={isDark}
      />
    </Container>
  );
};

export default ContactInquiries;
